import {
  Goal,
  ID,
  LifeArea,
  PrefixedID,
  Relationship,
  Tag,
} from '@griply/types';

import { buildRelationshipsMap } from './build-relationships-map';
import { getArchivedGoalIds } from './derived-goal-state';
import { buildGoalsById, getGoalLifeArea } from './get-goal-life-area';
import { filterGoalsByLifeArea } from './goals/filter-goals-by-filter-document';
import { buildEntityLinks } from './links/build-entity-links';

export type FilterSlices = {
  tagIdsSet: Set<ID>;
  lifeAreaIdsSet: Set<ID>;
  goalIdsSet: Set<ID>;
  relationshipsMap: Record<PrefixedID, PrefixedID[]>;
  archivedGoalIds: ID[];
  lifeAreaLinkedGoalIds: ID[];
  lifeAreaGoalIdsMap: Map<ID, ID[]>;
};

export const buildFilterSlices = (
  goals: Goal[],
  tags: Tag[],
  relationships: Relationship[],
  lifeAreas: LifeArea[],
): FilterSlices => {
  const tagIdsSet = new Set(tags.map((tag) => tag.id));
  const lifeAreaIdsSet = new Set(lifeAreas.map((lifeArea) => lifeArea.id));
  const goalIdsSet = new Set(goals.map((goal) => goal.id));
  const relationshipsMap = buildRelationshipsMap(relationships);
  const links = buildEntityLinks(relationships);

  const archivedGoalIds = [...getArchivedGoalIds(goals, { links })];

  // Every goal whose chain resolves to a life area — feeds the "No life area"
  // filter, which must also exclude tasks that inherit a life area from a goal.
  const goalsById = buildGoalsById(goals);
  const lifeAreaCache = new Map<ID, ID[]>();
  const lifeAreaLinkedGoalIds = goals
    .filter((goal) =>
      getGoalLifeArea(goal, { goalsById, links, cache: lifeAreaCache }),
    )
    .map(({ id }) => id);

  const lifeAreaGoalIdsMap = new Map<ID, ID[]>(
    lifeAreas.map(({ id }): [ID, ID[]] => [
      id,
      filterGoalsByLifeArea(goals, [id], links).map((goal) => goal.id),
    ]),
  );

  return {
    tagIdsSet,
    lifeAreaIdsSet,
    goalIdsSet,
    relationshipsMap,
    archivedGoalIds,
    lifeAreaLinkedGoalIds,
    lifeAreaGoalIdsMap,
  };
};
